import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import PublicShell from "@/components/public/PublicShell";
import {
    ShieldCheck, Lock, KeyRound, Users, Fingerprint, EyeOff, FileText,
    ServerCog, Trash2, Bug, AlertTriangle, ArrowRight, Info,
} from "lucide-react";

/**
 * VAULT · Trust — the public "why should I trust you with my financial life"
 * page. Every claim below is backed by a real production implementation.
 * Anything not implemented is either omitted or explicitly flagged.
 *
 * Implementation matrix (kept in comments for reviewer sanity):
 *   Passkeys / WebAuthn   → routes/webauthn.py (FEATURE_KEY passkey_authentication)
 *   Password + session    → routes/auth.py (bcrypt cost 12 + JWT)
 *   Re-auth for sensitive → webauthn.py `/passkey/reauth`
 *   Data export           → routes/data_export.py (GET /api/data-export)
 *   Audit log             → audit.py (log_action across routes)
 *   Tenant isolation      → tenant.py t(user) filter applied to every list/read/write
 *   Together zones        → data/demo_fixtures.js couple demo (Mine / Partner / Joint / Together)
 *   Vulnerability report  → /security/report-vulnerability route (public)
 *   Subprocessors page    → /subprocessors (public PublicPage)
 *
 * Deliberately NOT claimed (no verified implementation):
 *   SOC 2 / ISO 27001 / PCI-DSS  — no audit performed
 *   "bank-grade" / "military-grade" — marketing language, not a standard
 *   24/7 SOC monitoring — no such team today
 */
export default function TrustVault() {
    return (
        <PublicShell>
            <Helmet>
                <title>Trust · VAULT by PD</title>
                <meta name="description" content="How VAULT protects your financial information — what we store, what we never store, and the exact controls in place today." />
                <link rel="canonical" href="https://vaultbypd.com/trust" />
            </Helmet>
            <div className="tv-root" data-testid="trust-vault">
                <style>{TV_CSS}</style>

                {/* Hero — calm, not alarming. */}
                <section className="tv-container tv-hero">
                    <div className="tv-eyebrow"><ShieldCheck size={11}/> TRUST · CURRENT AS OF FEB 2026</div>
                    <h1 className="tv-title">Your financial life deserves the quiet kind of security.</h1>
                    <p className="tv-lede">
                        We only publish security claims we can prove today. This page describes exactly what
                        VAULT does, what it never does, and where each control sits in the product.
                    </p>
                </section>

                {/* Data we never store — first, and most important. */}
                <Section id="never-store" icon={EyeOff} eyebrow="What we never store"
                         title="The things VAULT never asks for.">
                    <p className="tv-p">
                        VAULT organises your financial <b>information</b>. It is not a payment system, it is
                        not a bank client, and it does not attempt to move money on your behalf. That means
                        we never need — and we never store:
                    </p>
                    <ul className="tv-list">
                        <li><b>CVV</b> or card security code</li>
                        <li><b>PIN</b> for any card, account or app</li>
                        <li><b>OTP</b> from your bank, wallet or card issuer</li>
                        <li><b>Internet-banking passwords</b> or app credentials</li>
                        <li><b>Full card numbers</b> — VAULT only stores the last 4 digits for identification</li>
                    </ul>
                    <div className="tv-callout">
                        <Info size={13}/> If any interface ever asks you for a CVV, PIN or OTP, it is not
                        a legitimate VAULT screen. Please report it to <a href="mailto:security@vaultbypd.com">security@vaultbypd.com</a>.
                    </div>
                </Section>

                {/* Account security — only what's implemented. */}
                <Section id="account-security" icon={KeyRound} eyebrow="Account security"
                         title="How you sign in — and stay signed in safely.">
                    <div className="tv-grid tv-grid--2">
                        <Card icon={Fingerprint} title="Passkeys (biometric auth)"
                              body="Sign in with Face ID, Touch ID, Windows Hello or a hardware key. Passkeys are the WebAuthn / FIDO2 standard — there is no shared secret to steal or phish." />
                        <Card icon={Lock} title="Password + bcrypt"
                              body="Passwords are salted and hashed with bcrypt (cost 12) before they ever hit the database. We can never read your password, only verify it." />
                        <Card icon={ShieldCheck} title="Re-auth for sensitive actions"
                              body="Removing a passkey, exporting data or changing security controls requires a fresh authentication — even if you're already signed in." />
                        <Card icon={ServerCog} title="Session tokens with short lifetime"
                              body="Access tokens live 30 minutes; refresh tokens rotate every 7 days. Sign out of one browser and every session on that device ends." />
                    </div>
                </Section>

                {/* Privacy by design — Mine/Partner/Joint/Together. */}
                <Section id="together-privacy" icon={Users} eyebrow="Privacy by design"
                         title="Mine. Partner. Joint. Together — with real boundaries.">
                    <p className="tv-p">
                        VAULT Together is built around four privacy zones. What lives in one zone is not
                        visible to another unless you explicitly move it.
                    </p>
                    <div className="tv-zones">
                        {[
                            { k: "Mine",     v: "Private to you. Nobody else on your household — not even your partner — can see it." },
                            { k: "Partner",  v: "Private to your partner. You cannot see it unless they choose to share it with you." },
                            { k: "Joint",    v: "Financial items you own or manage together — home loan, joint accounts, shared subscriptions." },
                            { k: "Together", v: "Household-level summary of the shared items. No private items leak into Together." },
                        ].map((z) => (
                            <div key={z.k} className="tv-zone">
                                <div className="tv-zone__label">{z.k}</div>
                                <div className="tv-zone__body">{z.v}</div>
                            </div>
                        ))}
                    </div>
                    <p className="tv-p tv-muted">
                        Family plans extend the same model — every family member has their own private
                        zone, and the shared household zone shows only what everyone has opted to share.
                    </p>
                </Section>

                {/* Data protection — implementation you can verify. */}
                <Section id="data-protection" icon={ServerCog} eyebrow="Data protection"
                         title="What actually protects your data today.">
                    <div className="tv-grid tv-grid--2">
                        <Card icon={Lock} title="TLS 1.2+ in transit"
                              body="Every request between your browser, our API and our database is encrypted in transit. HTTPS is enforced; plain-HTTP traffic is rejected at the edge." />
                        <Card icon={ShieldCheck} title="Workspace isolation"
                              body="Every record is scoped to a tenant_id filter server-side. A finance user in one household cannot query another household's data, even by ID." />
                        <Card icon={FileText} title="Complete audit log"
                              body="Every create / edit / delete / login / export event is written to an immutable audit log we can replay if you ever need to know what happened." />
                        <Card icon={Users} title="Role-based access"
                              body="Admin, Finance, Expense-user and Viewer each see only what their role allows. Cross-role privilege escalation is enforced at the endpoint, not in the UI." />
                    </div>
                    <div className="tv-callout tv-callout--warn">
                        <AlertTriangle size={13}/> <b>Not claimed:</b> field-level encryption at rest, SOC 2,
                        ISO 27001 and PCI-DSS certifications. These are on our roadmap; we do not claim
                        them until an independent auditor has signed the report.
                    </div>
                </Section>

                {/* Your controls. */}
                <Section id="your-controls" icon={KeyRound} eyebrow="Your controls"
                         title="You can see everything, and you can leave with everything.">
                    <div className="tv-grid tv-grid--3">
                        <Card icon={FileText} title="Export your data"
                              body="Download every record in your workspace as JSON, any time, from Settings → Data Export. Nothing is held hostage." />
                        <Card icon={Trash2} title="Delete your account"
                              body="Delete your workspace and every associated record from Settings → Account. Deletions are queued for 24 hours in case you change your mind, then purged." />
                        <Card icon={EyeOff} title="Privacy Mode"
                              body="Hide balances and card numbers with a single tap — useful when you're screen-sharing or on a train. Nothing about you appears in browser history." />
                    </div>
                </Section>

                {/* Responsible disclosure. */}
                <Section id="disclosure" icon={Bug} eyebrow="Responsible disclosure"
                         title="If you find a security issue, we want to hear from you.">
                    <p className="tv-p">
                        Email <a href="mailto:security@vaultbypd.com">security@vaultbypd.com</a> with steps
                        to reproduce, expected impact and any supporting artefacts. We acknowledge every
                        report within 3 business days. Please do not test on live customer workspaces or
                        run destructive scans — the demo at <Link to="/demo">/demo</Link> is a safe
                        target.
                    </p>
                    <div className="tv-ctas">
                        <Link to="/security/report-vulnerability" className="tv-cta">
                            Report a vulnerability <ArrowRight size={13}/>
                        </Link>
                        <Link to="/subprocessors" className="tv-cta tv-cta--ghost">
                            View subprocessor register
                        </Link>
                    </div>
                </Section>

                {/* Data deletion + retention. */}
                <Section id="deletion" icon={Trash2} eyebrow="Deletion & retention"
                         title="What happens when you leave.">
                    <ul className="tv-list">
                        <li><b>Live records:</b> deleted immediately from primary storage.</li>
                        <li><b>Backups:</b> encrypted rolling backups are overwritten within 35 days.</li>
                        <li><b>Audit log:</b> retained for one year for security-forensics purposes, then purged.</li>
                        <li><b>Billing records:</b> retained for the period local law requires (typically 7 years in India).</li>
                    </ul>
                </Section>

                {/* Closing CTA. */}
                <section className="tv-container tv-close">
                    <div className="tv-close__box">
                        <div className="tv-close__eyebrow">READY WHEN YOU ARE</div>
                        <div className="tv-close__title">Explore before you sign up.</div>
                        <p className="tv-close__body">
                            Try the demo with synthetic data — no account, no card, no session. Or start
                            the free trial when you're ready. Cancel any time.
                        </p>
                        <div className="tv-ctas">
                            <Link to="/signup" className="tv-cta tv-cta--gold">Start Free Trial <ArrowRight size={13}/></Link>
                            <Link to="/demo" className="tv-cta">Explore Demo</Link>
                        </div>
                    </div>
                </section>
            </div>
        </PublicShell>
    );
}

const Section = ({ id, icon: Icon, eyebrow, title, children }) => (
    <section id={id} className="tv-container tv-section" data-testid={`trust-${id}`}>
        <div className="tv-section__head">
            <div className="tv-section__icon"><Icon size={16}/></div>
            <div>
                <div className="tv-eyebrow tv-eyebrow--sm">{eyebrow.toUpperCase()}</div>
                <h2 className="tv-h2">{title}</h2>
            </div>
        </div>
        <div className="tv-section__body">{children}</div>
    </section>
);

const Card = ({ icon: Icon, title, body }) => (
    <div className="tv-card">
        <div className="tv-card__icon"><Icon size={14}/></div>
        <div className="tv-card__title">{title}</div>
        <p className="tv-card__body">{body}</p>
    </div>
);

const TV_CSS = `
.tv-root { background: #FBF7EE; color: #0F1E3A; min-height: 100vh; padding-bottom: 96px; }
.tv-container { max-width: 1080px; margin: 0 auto; padding: 0 28px; }
.tv-hero { padding: 72px 28px 32px; max-width: 820px; }
.tv-eyebrow { display: inline-flex; align-items: center; gap: 8px; font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 14px; }
.tv-eyebrow--sm { margin-bottom: 4px; }
.tv-title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(38px, 5.5vw, 56px); font-weight: 500; letter-spacing: -0.02em; line-height: 1.05; margin: 0 0 18px; }
.tv-lede { font-size: 16px; line-height: 1.6; color: #57534E; max-width: 640px; margin: 0; }
.tv-section { padding: 40px 28px; border-top: 1px solid rgba(15,30,58,0.08); }
.tv-section__head { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 22px; }
.tv-section__icon { width: 36px; height: 36px; border-radius: 12px; background: rgba(198,168,107,0.16); color: #C6A86B; display: grid; place-items: center; flex-shrink: 0; margin-top: 2px; }
.tv-h2 { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(24px, 3vw, 34px); font-weight: 500; letter-spacing: -0.015em; margin: 0; line-height: 1.15; color: #0F1E3A; }
.tv-p { font-size: 15px; line-height: 1.65; color: #44403C; margin: 0 0 14px; max-width: 720px; }
.tv-p a { color: #0F1E3A; font-weight: 600; text-decoration: underline; }
.tv-muted { color: #78716C; font-size: 13.5px; }
.tv-list { font-size: 14.5px; line-height: 1.7; color: #44403C; padding-left: 20px; margin: 0 0 14px; max-width: 720px; }
.tv-list li { margin: 4px 0; }
.tv-grid { display: grid; gap: 14px; }
.tv-grid--2 { grid-template-columns: repeat(2, 1fr); }
.tv-grid--3 { grid-template-columns: repeat(3, 1fr); }
@media (max-width: 780px) { .tv-grid--2, .tv-grid--3 { grid-template-columns: 1fr; } }
.tv-card { background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 16px; padding: 18px 18px 20px; }
.tv-card__icon { width: 30px; height: 30px; border-radius: 10px; background: rgba(15,30,58,0.06); color: #0F1E3A; display: grid; place-items: center; margin-bottom: 12px; }
.tv-card__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 20px; font-weight: 500; letter-spacing: -0.01em; margin: 0 0 6px; color: #0F1E3A; }
.tv-card__body { font-size: 13.5px; line-height: 1.55; color: #57534E; margin: 0; }
.tv-callout { display: flex; align-items: flex-start; gap: 8px; background: rgba(198,168,107,0.09); border: 1px solid rgba(198,168,107,0.28); border-radius: 12px; padding: 12px 14px; font-size: 13.5px; color: #44403C; margin-top: 14px; max-width: 720px; line-height: 1.55; }
.tv-callout--warn { background: rgba(217,119,6,0.06); border-color: rgba(217,119,6,0.28); }
.tv-zones { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; margin: 4px 0 14px; }
@media (max-width: 900px) { .tv-zones { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 520px) { .tv-zones { grid-template-columns: 1fr; } }
.tv-zone { background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 14px; padding: 14px; }
.tv-zone__label { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 18px; font-weight: 600; color: #0F1E3A; margin-bottom: 4px; }
.tv-zone__body { font-size: 13px; line-height: 1.55; color: #57534E; }
.tv-ctas { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 12px; }
.tv-cta { display: inline-flex; align-items: center; gap: 6px; padding: 10px 18px; border-radius: 999px; font-size: 13px; font-weight: 600; text-decoration: none; background: #0F1E3A; color: #F6F3EC; }
.tv-cta:hover { background: #152a52; }
.tv-cta--ghost { background: transparent; color: #0F1E3A; border: 1px solid rgba(15,30,58,0.28); }
.tv-cta--ghost:hover { border-color: #0F1E3A; }
.tv-cta--gold { background: linear-gradient(180deg,#E6D5A8,#C6A86B); color: #0F1E3A; box-shadow: 0 12px 26px -8px rgba(198,168,107,0.55); }
.tv-close { margin-top: 40px; }
.tv-close__box { background: #0F1E3A; color: #F6F3EC; border-radius: 22px; padding: 32px 32px 30px; border: 1px solid rgba(230,213,168,0.22); }
.tv-close__eyebrow { font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #E6D5A8; margin-bottom: 6px; }
.tv-close__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 26px; font-weight: 500; letter-spacing: -0.015em; }
.tv-close__body { font-size: 14px; color: rgba(241,245,249,0.85); margin: 8px 0 16px; max-width: 560px; line-height: 1.55; }
.tv-close .tv-cta { background: rgba(255,255,255,0.08); color: #F6F3EC; border: 1px solid rgba(230,213,168,0.35); }
.tv-close .tv-cta--gold { background: linear-gradient(180deg,#E6D5A8,#C6A86B); color: #0F1E3A; border: none; }
`;
